/**
 * LocationAutocomplete Component
 *
 * Autocomplete input för att söka efter platser med:
 * - Debounced geocoding search
 * - Keyboard navigation (piltangenter + Enter)
 * - Click outside för att stänga dropdown
 * - Loading spinner
 * - Färgad indikator (grön/röd)
 * - ARIA-attribut för tillgänglighet
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';
import type { GeocodingResult } from '@/types';
import { useGeocoding } from '@/hooks/useGeocoding';

export interface LocationAutocompleteProps {
  value: GeocodingResult | null;
  onChange: (result: GeocodingResult | null) => void;
  placeholder: string;
  label?: string;
  indicatorColor?: 'green' | 'red';
}

const MAX_RESULTS = 5;

/**
 * Formatera geocoding-resultat för visning
 * Format: "Stockholm, Stockholms län" eller "Stockholm, Sweden"
 */
function formatLocation(result: GeocodingResult): string {
  const parts = [result.name];

  if (result.region) {
    parts.push(result.region);
  } else if (result.country) {
    parts.push(result.country);
  }

  return parts.join(', ');
}

export function LocationAutocomplete({
  value,
  onChange,
  placeholder,
  label,
  indicatorColor = 'green',
}: LocationAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { results, isLoading, error, search, clear } = useGeocoding();

  // Begränsa resultat till MAX_RESULTS
  const displayResults = results.slice(0, MAX_RESULTS);

  /**
   * Uppdatera input-värde när value prop ändras
   */
  useEffect(() => {
    if (value) {
      setInputValue(formatLocation(value));
      setIsOpen(false);
    } else {
      setInputValue('');
    }
  }, [value]);

  /**
   * Hantera input-ändringar
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Rensa valt värde om användaren ändrar input
    if (value) {
      onChange(null);
    }

    // Starta sökning
    search(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  /**
   * Välj ett resultat
   */
  const selectResult = useCallback((result: GeocodingResult) => {
    onChange(result);
    setInputValue(formatLocation(result));
    setIsOpen(false);
    setHighlightedIndex(-1);
    clear();
  }, [onChange, clear]);

  /**
   * Rensa input och valt värde
   */
  const handleClear = () => {
    setInputValue('');
    onChange(null);
    clear();
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  /**
   * Hantera tangentbordsinput
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < displayResults.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < displayResults.length) {
          const result = displayResults[highlightedIndex];
          if (result) {
            selectResult(result);
          }
        }
        break;

      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  /**
   * Click outside handler
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Scrolla highlighted item into view
   */
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const indicatorColorClass = indicatorColor === 'green'
    ? 'bg-green-500'
    : 'bg-red-500';

  const showDropdown = isOpen && (displayResults.length > 0 || isLoading || !!error);

  return (
    <div className="relative">
      {/* Label */}
      {label && (
        <label
          htmlFor={`location-input-${indicatorColor}`}
          className="block text-sm font-bold text-gray-700 mb-1"
        >
          {label}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Färgad indikator */}
        <div
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${indicatorColorClass}`}
          aria-hidden="true"
        />

        {/* Input field */}
        <input
          ref={inputRef}
          id={`location-input-${indicatorColor}`}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (displayResults.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="input pl-10 pr-10"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={!!showDropdown}
          aria-controls={`location-dropdown-${indicatorColor}`}
          aria-label={label || placeholder}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `location-option-${indicatorColor}-${highlightedIndex}`
              : undefined
          }
        />

        {/* Loading spinner eller clear-knapp */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          ) : inputValue ? (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Rensa"
              type="button"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          id={`location-dropdown-${indicatorColor}`}
          className="absolute z-[100] w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
          role="listbox"
        >
          {/* Resultat */}
          {displayResults.map((result, index) => (
            <button
              key={`${result.name}-${result.coordinates.lat}-${result.coordinates.lon}`}
              data-index={index}
              id={`location-option-${indicatorColor}-${index}`}
              onClick={() => selectResult(result)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`
                w-full px-4 py-3 text-left flex items-start gap-3
                transition-colors border-b border-gray-100 last:border-b-0
                ${highlightedIndex === index
                  ? 'bg-blue-50'
                  : 'hover:bg-gray-50'
                }
              `}
              role="option"
              aria-selected={highlightedIndex === index}
              type="button"
            >
              <MapPin
                className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  highlightedIndex === index ? 'text-blue-600' : 'text-gray-400'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {result.name}
                </div>
                {(result.region || result.country) && (
                  <div className="text-sm text-gray-500 truncate">
                    {result.region || result.country}
                  </div>
                )}
              </div>
            </button>
          ))}

          {/* Felmeddelande */}
          {error && !isLoading && (
            <div className="px-4 py-3 text-sm text-red-600">
              {error.message || 'Kunde inte söka efter platser'}
            </div>
          )}

          {/* Inga resultat */}
          {!isLoading && !error && displayResults.length === 0 && inputValue.length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              Inga platser hittades
            </div>
          )}
        </div>
      )}
    </div>
  );
}
